// =============================================================================
// Module Project - Hook: useCommandPaletteStore
// Gestion de la recherche pour la command palette (Module Gestion Projet)
// =============================================================================

import { useState, useCallback } from 'react';
import { searchActiveProjects, searchOrganizationMembers, type ProjectSearchResult, type MemberSearchResult } from '../data/projectRepository';
import { searchTasksWithProject, type TaskSearchResult } from '../data/taskRepository';
import { searchMilestones, type MilestoneSearchResult } from '../data/milestoneRepository';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
export interface CommandPaletteState {
  tasks: TaskSearchResult[];
  projects: ProjectSearchResult[];
  milestones: MilestoneSearchResult[];
  members: MemberSearchResult[];
  loading: boolean;
  error: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────────────────────────────────────
export function useCommandPaletteStore() {
  const [state, setState] = useState<CommandPaletteState>({
    tasks: [],
    projects: [],
    milestones: [],
    members: [],
    loading: false,
    error: null,
  });

  // ───────────────────────────────────────────────────────────────────────────
  // SEARCH
  // ───────────────────────────────────────────────────────────────────────────
  const search = useCallback(async (orgId: string, query: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Parallel fetches for all entity types
      const [projects, tasks, milestones, members] = await Promise.all([
        searchActiveProjects(orgId, query, 5),
        query.length > 0 ? searchTasksWithProject(orgId, query, 8) : Promise.resolve([]),
        query.length > 0 ? searchMilestones(orgId, query, 5) : Promise.resolve([]),
        query.length > 0 ? searchOrganizationMembers(orgId, query, 5) : Promise.resolve([]),
      ]);

      setState({
        projects,
        tasks,
        milestones,
        members,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('Command palette search error:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Erreur lors de la recherche',
      }));
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // RESET
  // ───────────────────────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    setState({
      tasks: [],
      projects: [],
      milestones: [],
      members: [],
      loading: false,
      error: null,
    });
  }, []);

  return {
    ...state,
    search,
    reset,
  };
}
